// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Landbridge — one round of a live race, decoupled from Trystero, the DOM and the
// clock (driven by play()/undo()/tick(), which is what makes the host-transfer
// contract testable without a network — tests/takeover.test.ts).
//
// PARALLEL SAME-SEED RACE: every peer holds its own copy of the identical puzzle (a
// pure function of the frozen seed) and solves it independently. NO game state
// crosses the wire — only a tiny status gossip (hops, misfires, solved/over, and a
// final route trail for the results). Each peer reaches its OWN game-over (arrive /
// out of lives / stuck / local timer) with no help from the host, so HOST TRANSFER
// IS A PURE DISPLAY CONCERN: a promoted peer keeps relaying gossip and still
// finishes. onHostChange is wired regardless.

import {
  bestProgress,
  createGame,
  giveUp,
  markTimeout,
  play,
  takeHint,
  undo,
  type GameState,
} from './game';
import type { Mode } from './modes';

export interface PeerResult {
  seat: number;
  name: string;
  solved: boolean;
  over: boolean;
  hops: number;
  misfires: number;
  chainLen: number;
  /** Route: min BFS distance to the destination ever reached (lower = closer). */
  progress: number;
  /** Full chain (country ids) — populated once the peer finishes. */
  trail: string[];
  left: boolean;
}

/** Wire status. Tiny; the trail rides only on the final message. */
export interface RaceMsg {
  h: number; // hops
  m: number; // misfires
  s: boolean; // solved
  d: boolean; // done
  c: number; // chainLen
  p: number; // progress
  trail?: string[];
}

export interface RaceDeps {
  send: (m: RaceMsg) => void;
  onChange: () => void;
  onSelfEnd: (g: GameState) => void;
}

export interface RaceOpts {
  mode: Mode;
  seed: number;
  selfSeat: number | null;
  names: string[];
  isHost: boolean;
  deps: RaceDeps;
}

export class Race {
  state: GameState;
  readonly selfSeat: number | null;
  readonly mode: Mode;
  private host: boolean;
  private readonly deps: RaceDeps;
  private timeLeft: number;
  private ended = false;
  readonly results = new Map<number, PeerResult>();

  constructor(o: RaceOpts) {
    this.mode = o.mode;
    this.state = createGame(o.mode, o.seed);
    this.selfSeat = o.selfSeat;
    this.host = o.isHost;
    this.deps = o.deps;
    this.timeLeft = o.mode.timeMs;
    o.names.forEach((name, seat) => {
      this.results.set(seat, {
        seat,
        name,
        solved: false,
        over: false,
        hops: 0,
        misfires: 0,
        chainLen: 1,
        progress: Infinity,
        trail: [],
        left: false,
      });
    });
    if (o.selfSeat !== null) this.syncSelf();
  }

  get isHost(): boolean {
    return this.host;
  }
  get msLeft(): number {
    return Math.max(0, this.timeLeft);
  }

  private self(): PeerResult | undefined {
    return this.selfSeat === null ? undefined : this.results.get(this.selfSeat);
  }

  private syncSelf(): void {
    const r = this.self();
    if (!r) return;
    r.hops = this.state.chain.length - 1;
    r.misfires = this.state.misfires;
    r.chainLen = this.state.chain.length;
    r.solved = this.state.solved;
    r.over = this.state.over;
    r.progress = this.mode.objective === 'route' ? bestProgress(this.state) : 0;
    if (this.state.over) r.trail = this.state.chain.slice();
  }

  /** Play a local hop; returns whether it changed anything. */
  guess(id: string): boolean {
    if (this.selfSeat === null || this.state.over) return false;
    const next = play(this.state, id);
    if (next === this.state) return false;
    this.state = next;
    this.syncSelf();
    if (this.state.over) return this.finishSelf();
    this.broadcastProgress();
    this.deps.onChange();
    return true;
  }

  /** Undo the last hop (route modes). */
  undoHop(): boolean {
    if (this.selfSeat === null) return false;
    const next = undo(this.state);
    if (next === this.state) return false;
    this.state = next;
    this.syncSelf();
    this.broadcastProgress();
    this.deps.onChange();
    return true;
  }

  /**
   * Spend this round's one hint. Purely local: nothing a peer can see changes, so
   * there is nothing to gossip and the wire shape stays frozen.
   */
  hint(): boolean {
    if (this.selfSeat === null) return false;
    const next = takeHint(this.state);
    if (next === this.state) return false;
    this.state = next;
    return true;
  }

  /**
   * Concede the round. It MUST end through finishSelf() like every other ending —
   * otherwise the other peers never hear "done" and sit on "still routing" until
   * the 20s close-wait expires.
   */
  concede(): void {
    if (this.selfSeat === null || this.state.over) return;
    this.state = giveUp(this.state);
    this.syncSelf();
    this.finishSelf();
  }

  private broadcastProgress(): void {
    const r = this.self();
    if (!r) return;
    this.deps.send({ h: r.hops, m: r.misfires, s: r.solved, d: false, c: r.chainLen, p: r.progress });
  }

  private finishSelf(): boolean {
    if (this.ended) return true;
    this.ended = true;
    this.syncSelf();
    const r = this.self();
    if (r) {
      this.deps.send({ h: r.hops, m: r.misfires, s: r.solved, d: true, c: r.chainLen, p: r.progress, trail: r.trail });
    }
    this.deps.onChange();
    this.deps.onSelfEnd(this.state);
    return true;
  }

  /** A status update from another peer. */
  receive(msg: RaceMsg, fromSeat: number): void {
    const r = this.results.get(fromSeat);
    if (!r || r.seat === this.selfSeat) return;
    r.hops = msg.h;
    r.misfires = msg.m;
    r.chainLen = msg.c;
    r.solved = msg.s;
    r.progress = Math.min(r.progress, msg.p);
    r.over = msg.d || r.over;
    if (msg.trail) {
      r.trail = msg.trail;
      r.over = true;
    }
    this.deps.onChange();
  }

  /** Interval-driven clock (never rAF alone). Every seated peer runs its own. */
  tick(dtMs: number): void {
    if (this.selfSeat === null || this.state.over) return;
    this.timeLeft -= dtMs;
    if (this.timeLeft <= 0) {
      this.state = markTimeout(this.state);
      this.syncSelf();
      this.finishSelf();
    }
  }

  /** Promotion carries no authoritative duty here — recorded for display only. */
  setHost(isHost: boolean): void {
    this.host = isHost;
  }

  peerLeft(seat: number): void {
    const r = this.results.get(seat);
    if (!r || seat === this.selfSeat) return;
    r.left = true;
    r.over = true; // a departed peer will never finish; don't hold the room for it
    this.deps.onChange();
  }
  peerReturned(seat: number): void {
    const r = this.results.get(seat);
    if (r) r.left = false;
  }

  everyoneDone(): boolean {
    return [...this.results.values()].every((r) => r.over);
  }

  /** Results best-first, objective-aware. */
  ranked(): PeerResult[] {
    const route = this.mode.objective === 'route';
    return [...this.results.values()].sort((a, b) => {
      if (a.left !== b.left) return a.left ? 1 : -1;
      if (route) {
        if (a.solved !== b.solved) return a.solved ? -1 : 1;
        if (a.solved && b.solved) return a.hops - b.hops || a.misfires - b.misfires;
        // Both unsolved: whoever got closer (lower progress), then fewer misfires.
        return a.progress - b.progress || a.misfires - b.misfires;
      }
      // Chain: longest chain wins, then fewer misfires.
      return b.chainLen - a.chainLen || a.misfires - b.misfires;
    });
  }
}

/** Seat index from the frozen roster, or null for a spectator / unknown peer. */
export function seatOf(roster: Array<{ id: string }>, peerId: string): number | null {
  const i = roster.findIndex((p) => p.id === peerId);
  return i < 0 ? null : i;
}

/** A one-shot timer safe to poke repeatedly — re-arming on every poll starves it. */
export function createOneShot(delayMs: number, run: () => void) {
  let id: ReturnType<typeof setTimeout> | null = null;
  return {
    poke(): void {
      if (id !== null) return;
      id = setTimeout(() => {
        id = null;
        run();
      }, delayMs);
    },
    cancel(): void {
      if (id !== null) clearTimeout(id);
      id = null;
    },
  };
}
